import {Header, Tabs} from '@amzn/awsui-components-react';
import Box from '@amzn/awsui-components-react/polaris/box';
import ExpandableSection from '@amzn/awsui-components-react/polaris/expandable-section';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import React from 'react';
import {RouteComponentProps, withRouter} from 'react-router-dom';
import {CheckDynamicExecutionResponse, MappingStatus} from '../../../backend/model';
import {prettifyDateLocal, prettifyDateUTC} from '../../workflow/date';
import IapmTable from '../tables/IapmTable';
import IoglTable from '../tables/IoglTable';
import WorkflowStatusIndicator from '../../workflow/WorkflowStatusIndicator';
import SyntaxHighlighter from 'react-syntax-highlighter';
import {RuleListLink, WorkflowHistoryExecutionIdLink} from '../../TableItemLink';
import {prettifyJSON} from '../../../utils/jsonHelper';

interface DynamicExecutionDetailsPanelProps extends RouteComponentProps {
    response: CheckDynamicExecutionResponse;
}

const DynamicExecutionDetailsPanel: React.FC<DynamicExecutionDetailsPanelProps> = (props) => {
    const dynamicExecution = props.response.execution

    const gridDef = Array(15).fill([
        {colspan: {default: 6, xxs: 1}},
        {colspan: {default: 6, xxs: 5}},
    ]).flat()

    const summary = (
        <>
            <Grid
                disableGutters
                gridDefinition={gridDef}>
                <Box variant='p'>Execution ID:</Box>
                <Box variant='p' id='whdp-executionId'>{dynamicExecution.executionId}</Box>

                <Box variant='p'>Status: </Box>
                <Box id='whdp-status'><WorkflowStatusIndicator status={dynamicExecution.status}/></Box>

                <Box variant='p'>Source: </Box>
                {dynamicExecution.source?.includes("AvailabilityMappingOrchestrationService") ?
                    <Box id='whdp-source'>
                        {dynamicExecution.source.slice(0,dynamicExecution.source.search(":")+1)}
                        <WorkflowHistoryExecutionIdLink executionId={dynamicExecution.source.slice(dynamicExecution.source.search(":")+1,)}/>
                    </Box> :
                <Box variant='p' id='whdp-source'>{dynamicExecution.source}</Box>}

                <Box variant='p'>Version: </Box>
                <Box variant='p' id='whdp-version'>{dynamicExecution.version}</Box>

                <Box variant='p'>Client Name: </Box>
                <Box variant='p' id='whdp-clientName'>{dynamicExecution.clientName}</Box>

                <Box variant='p'>Requester: </Box>
                <Box variant='p' id='whdp-requester'>{dynamicExecution.requester}</Box>

                <Box variant='p'>Started At: </Box>
                <Box variant='p' id='whdp-createTime-utc'>
                    {prettifyDateUTC(new Date(dynamicExecution.createTime))}
                </Box>
                <Box variant='p'>Last Modified At: </Box>
                <Box variant='p' id='whdp-lastModifiedTime-utc'>
                    {prettifyDateUTC(new Date(dynamicExecution.lastModifiedTime))}
                </Box>

                <Box></Box>
                <Box variant='p' id='whdp-createTime-local'>
                    {prettifyDateLocal(new Date(dynamicExecution.createTime))}
                </Box>
                <Box></Box>
                <Box variant='p' id='whdp-lastModifiedTime-local'>
                    {prettifyDateLocal(new Date(dynamicExecution.lastModifiedTime))}
                </Box>

                <Box variant='p'>Error Code: </Box>
                <Box variant='p' id='whdp-errorCode'>{dynamicExecution.errorCode}</Box>

                <Box variant='p'>Error Message: </Box>
                <Box variant='p' id='whdp-errorMessage'>{dynamicExecution.errorMessage}</Box>

                <Box variant='p'>Input: </Box>
                <SyntaxHighlighter id='whdp-input'
                                   customStyle={{background: 'transparent'}}
                                   language="json">
                    {prettifyJSON(dynamicExecution.input!)}
                </SyntaxHighlighter>

                <Box variant='p'>Rules: </Box>
                <RuleListLink rules={dynamicExecution.rules!} />
                
                <Box variant='p'>Is Dry Run: </Box>
                <Box variant='p' id='whdp-dryRun'>{dynamicExecution.dryRun.toString()}</Box>
            </Grid>
        </>
    );

    function getFileName(tableName: string): string {
        const fileNamePrefix = `DynamicExecution ${dynamicExecution.executionId}`
        return `${fileNamePrefix} ${tableName}`
    }

    const iapms = dynamicExecution.dryRun ?
        dynamicExecution.iapms!.filter(iapm => iapm.mappingStatus == MappingStatus.REQUESTED)
        : dynamicExecution.iapms!.filter(iapm => iapm.mappingStatus != MappingStatus.REQUESTED)

    const iogls = dynamicExecution.dryRun ?
        dynamicExecution.iogls!.filter(iogl => iogl.mappingStatus == MappingStatus.REQUESTED)
        : dynamicExecution.iogls!.filter(iogl => iogl.mappingStatus != MappingStatus.REQUESTED)

    const tabs =
        <Tabs
            tabs={[
                {
                    id: "iapm",
                    content: <IapmTable tableItems={iapms ?? []} fileName={getFileName("IAPMs")}/>,
                    label: "IAPMs",
                    disabled: !dynamicExecution.iapms,
                },
                {
                    id: "iogl",
                    content: <IoglTable tableItems={iogls ?? []} fileName={getFileName("IOGLs")}/>,
                    label: "IOGLs",
                    disabled: !dynamicExecution.iogls,
                },
            ]}
        />

    return (
        <div>
            <ExpandableSection
                id='dynamicExecutionDetailsPanel'
                variant='container'
                header={<Header variant='h2'>Details</Header>}
                defaultExpanded>
                {summary}
            </ExpandableSection>
            <br/>
            <br/>
            <ExpandableSection
                id='dynamicExecutionMappingsPanel'
                variant='container'
                header={<Header variant='h2'>Mappings</Header>}
                defaultExpanded>
                {tabs}
            </ExpandableSection>
        </div>
    );
};

export default withRouter(DynamicExecutionDetailsPanel);
